import React, { useState, useEffect, useRef, useCallback } from "react";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "react-toastify";

import "../../../style/returnsRegister.css";
import ActiveRegistrationIcon from "../../../assets/active-registration-icon.svg";
import CompletedIcon from "../../../assets/return-completed-icon.svg";
import UnderRepairIcon from "../../../assets/under-repair-icon.svg";
import NotRepairableIcon from "../../../assets/not-repairable.svg";
import ScanIcon from "../../../assets/icons/ScanIcon";
import AddIcon from "../../../assets/icons/AddIcon";
import FilterIcon from "../../../assets/icons/FilterIcon";
import PrintIcon from "../../../assets/icons/PrintIcon";
import SearchIcon from "../../../assets/search-icon.svg";
import EditIcon from "../../../assets/icons/EditIcon";
import NoDataIcon from "../../../assets/icons/NoDataIcon";
import RedDeleteIcon from "../../../assets/icons/RedDeleteIcon";
import PrintReturnRegister from "./components/PrintReturnRegister";
import DeleteModal from "./components/DeleteModal";
import AddManualReturnsModal from "./components/AddManualReturnsModal";
import { returnRegisterAPI } from "../../../services/index";

import CalendarIcon from "../../../assets/calender-icon.svg";
import { useSelector } from "react-redux";

/* ---------------- Tooltip Component ---------------- */
const Tooltip = ({ children, content, show }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top = triggerRect.bottom + 8;
      let left =
        triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;

      // Adjust if tooltip goes off screen
      if (left < 8) left = 8;
      if (left + tooltipRect.width > viewportWidth - 8) {
        left = viewportWidth - tooltipRect.width - 8;
      }
      if (top + tooltipRect.height > viewportHeight - 8) {
        top = triggerRect.top - tooltipRect.height - 8;
      }

      setPosition({ top, left });
    }
  }, [isVisible]);

  if (!show) return children;

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        style={{ cursor: "help" }}
      >
        {children}
      </span>
      {isVisible && (
        <div
          ref={tooltipRef}
          className="returns-tooltip"
          style={{
            position: "fixed",
            top: position.top,
            left: position.left,
            zIndex: 1000,
          }}
        >
          {content}
        </div>
      )}
    </>
  );
};

function RegisterReturns() {
  const { isPartsURL } = useSelector((state) => state.domain);
  const [, setReturns] = useState([]);
  const [filteredReturns, setFilteredReturns] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [, setShowForm] = useState(false);
  const [editingReturn, setEditingReturn] = useState(null);
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [balanceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("this_month");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showDateFilterDropdown, setShowDateFilterDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [returnToDelete, setReturnToDelete] = useState(null);
  const [showAddManualModal, setShowAddManualModal] = useState(false);
  const [returnStats, setReturnStats] = useState({
    total_count: 0,
    active_registration: 0,
    completed: 0,
    under_repair: 0,
    not_returnable: 0,
  });
  const [statsLoading, setStatsLoading] = useState(false);
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null); // Track which return's status dropdown is open
  const [updatingStatus, setUpdatingStatus] = useState(null); // Track which return's status is being updated
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 }); // Position for fixed dropdown
  const filterDropdownRef = useRef(null);
  const dateFilterDropdownRef = useRef(null);
  const statusDropdownRefs = useRef({}); // Refs for status dropdowns
  const statusTriggerRefs = useRef({}); // Refs for status trigger buttons

  // Ref to prevent double fetching in React StrictMode
  const hasLoadedInitialData = useRef(false);
  const previousFiltersRef = useRef({
    searchTerm,
    currentPage,
    statusFilter,
    dateFilter,
    balanceFilter,
  });

  // Fetch returns from API
  const fetchReturns = useCallback(async () => {
    setLoading(true);
    try {
      const type =
        balanceFilter === "positive"
          ? "credit"
          : balanceFilter === "negative"
          ? "debit"
          : "";

      // Map date filter value to human-readable label
      // Note: "this_month" is the default, so we send empty string to omit the parameter
      const dateFilterMap = {
        today: "Today",
        yesterday: "Yesterday",
        last_7_days: "Last 7 Days",
        this_month: "", // Default - don't send parameter
        all: "",
      };
      const dateFilterLabel =
        dateFilterMap[dateFilter] !== undefined
          ? dateFilterMap[dateFilter]
          : dateFilter;

      // Map status filter value to human-readable label
      const statusFilterMap = {
        all: "",
        in_process: "In Process",
        under_repair: "Under Repair",
        not_repairable: "Not Repairable",
        completed: "Completed",
        successfully_returned: "Successfully Returned",
        refund: "Refund",
      };
      const statusFilterLabel = statusFilterMap[statusFilter] || statusFilter;

      const result = await returnRegisterAPI.getReturnsList(
        searchTerm,
        currentPage,
        itemsPerPage,
        statusFilterLabel,
        dateFilterLabel,
        type
      );

      if (result.error) {
        toast.error(result.error.message || "Failed to fetch returns");
        setReturns([]);
        setFilteredReturns([]);
        setTotalCount(0);
        return;
      }

      // Handle paginated response structure
      const responseData = result.response || {};
      const returnsList =
        responseData.results ||
        responseData.data ||
        (Array.isArray(responseData) ? responseData : []);

      // Set total count for pagination if available
      const count =
        responseData.count || responseData.total_count || returnsList.length;
      setTotalCount(count);

      setReturns(returnsList);
      setFilteredReturns(returnsList); // API already filters, so use directly
    } catch (error) {
      console.error("Error fetching returns:", error);
      toast.error("Failed to fetch returns");
      setReturns([]);
      setFilteredReturns([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    searchTerm,
    currentPage,
    itemsPerPage,
    statusFilter,
    dateFilter,
    balanceFilter,
  ]);

  const fetchReturnStats = async () => {
    setStatsLoading(true);
    try {
      const result = await returnRegisterAPI.getReturnDevicesCount();
      if (result.error) {
        toast.error(
          result.error.message || "Failed to fetch return statistics"
        );
        // Set default values on error
        setReturnStats({
          total_count: 0,
          active_registration: 0,
          completed: 0,
          under_repair: 0,
          not_returnable: 0,
        });
      } else {
        const stats = result.response || {};
        setReturnStats({
          total_count: stats.total_count || 0,
          active_registration: stats.active_registration || 0,
          completed: stats.completed || 0,
          under_repair: stats.under_repair || 0,
          not_returnable: stats.not_returnable || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching return stats:", error);
      toast.error("Failed to fetch return statistics");
      // Set default values on error
      setReturnStats({
        total_count: 0,
        active_registration: 0,
        completed: 0,
        under_repair: 0,
        not_returnable: 0,
      });
    } finally {
      setStatsLoading(false);
    }
  };

  // Load stats and returns on component mount only (once, even with StrictMode)
  useEffect(() => {
    if (!hasLoadedInitialData.current) {
      hasLoadedInitialData.current = true;
      fetchReturnStats();
      fetchReturns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch returns when filters or pagination change (skip initial mount)
  useEffect(() => {
    // Skip if we haven't loaded initial data yet
    if (!hasLoadedInitialData.current) {
      return;
    }

    // Check if any filter actually changed
    const prev = previousFiltersRef.current;
    const hasChanged =
      prev.searchTerm !== searchTerm ||
      prev.currentPage !== currentPage ||
      prev.statusFilter !== statusFilter ||
      prev.dateFilter !== dateFilter ||
      prev.balanceFilter !== balanceFilter;

    // Update the ref with current values
    previousFiltersRef.current = {
      searchTerm,
      currentPage,
      statusFilter,
      dateFilter,
      balanceFilter,
    };

    // Only fetch if something actually changed
    if (hasChanged) {
      fetchReturns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, currentPage, statusFilter, dateFilter, balanceFilter]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target)
      ) {
        setShowFilterDropdown(false);
      }
      if (
        dateFilterDropdownRef.current &&
        !dateFilterDropdownRef.current.contains(event.target)
      ) {
        setShowDateFilterDropdown(false);
      }
      // Check if click is outside any status dropdown
      const clickedInsideStatusDropdown = Object.values(
        statusDropdownRefs.current
      ).some((ref) => ref && ref.contains(event.target));
      // Check if click is on the trigger button
      const clickedOnTrigger = Object.values(statusTriggerRefs.current).some(
        (ref) => ref && ref.contains(event.target)
      );
      if (
        !clickedInsideStatusDropdown &&
        !clickedOnTrigger &&
        openStatusDropdown !== null
      ) {
        setOpenStatusDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openStatusDropdown]);

  // Update dropdown position on scroll
  useEffect(() => {
    if (openStatusDropdown !== null) {
      const updatePosition = () => {
        const triggerEl = statusTriggerRefs.current[openStatusDropdown];
        if (triggerEl) {
          const rect = triggerEl.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + 4,
            left: rect.left,
          });
        }
      };

      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [openStatusDropdown]);

  // Handle search with debounce - reset to page 1 when search changes
  useEffect(() => {
    if (!hasLoadedInitialData.current) {
      return; // Skip on initial mount
    }

    const delayedSearch = setTimeout(() => {
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Use API statistics for summary cards
  const activeRegistrationCount = returnStats.active_registration || 0;
  const completedCount = returnStats.completed || 0;
  const underRepairCount = returnStats.under_repair || 0;
  const notRepairableCount = returnStats.not_returnable || 0;

  // Format numbers for display
  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-US").format(num || 0);
  };

  // Pagination - API handles pagination, so we use filteredReturns directly
  const currentReturns = filteredReturns;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const shouldShowPagination = totalCount > itemsPerPage && totalPages > 1;

  const SortableHeader = ({ field, children }) => (
    <th className="sortable-header" onClick={() => handleSort(field)}>
      {children}
      {sortField === field && (
        <span className="ms-1">{sortDirection === "asc" ? "↑" : "↓"}</span>
      )}
    </th>
  );

  // Format date for returns table (e.g., "Oct 15, 2025")
  const formatReturnDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const dateObj =
        typeof dateString === "string" ? new Date(dateString) : dateString;
      if (isNaN(dateObj.getTime())) return dateString || "-";

      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = months[dateObj.getMonth()];
      const day = dateObj.getDate();
      const year = dateObj.getFullYear();

      return `${month} ${day}, ${year}`;
    } catch (error) {
      return dateString || "-";
    }
  };

  // Truncate text for display with ellipsis
  const truncateText = (text, maxLength = 20) => {
    if (text === null || text === undefined) return "-";
    // Convert to string if not already a string
    const textString = String(text);
    if (textString.trim() === "") return "-";
    if (textString.length <= maxLength) return textString;
    return `${textString.substring(0, maxLength)}...`;
  };

  const statusFilterOptions = [
    { value: "all", label: "All Statuses" },
    { value: "in_process", label: "In Process" },
    { value: "under_repair", label: "Under Repair" },
    { value: "not_repairable", label: "Not Repairable" },
    { value: "completed", label: "Completed" },
    { value: "successfully_returned", label: "Successfully Returned" },
    { value: "refund", label: "Refund" },
  ];

  const dateFilterOptions = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "last_7_days", label: "Last 7 Days" },
    { value: "this_month", label: "This Month" },
  ];

  // Status options for dropdown
  const statusDropdownOptions = [
    { value: "In Process", label: "In Process" },
    { value: "Under Repair", label: "Under Repair" },
    { value: "Completed", label: "Completed" },
    { value: "Not Repairable", label: "Not Repairable" },
    { value: "Successfully Returned", label: "Successfully Returned" },
    { value: "Refund to Customer", label: "Refund to Customer" },
  ];

  const getStatusFilterLabel = () => {
    const option = statusFilterOptions.find(
      (opt) => opt.value === statusFilter
    );
    return option ? option.label : "All Statuses";
  };

  const handleStatusFilterSelect = (value) => {
    setStatusFilter(value);
    setShowFilterDropdown(false);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const getDateFilterLabel = () => {
    const option = dateFilterOptions.find((opt) => opt.value === dateFilter);
    return option ? option.label : "This Month";
  };

  const handleDateFilterSelect = (value) => {
    setDateFilter(value);
    setShowDateFilterDropdown(false);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handle status update
  const handleStatusUpdate = async (returnId, newStatus) => {
    setUpdatingStatus(returnId);
    try {
      const result = await returnRegisterAPI.updateReturnStatus(
        returnId,
        newStatus
      );
      if (result.error) {
        toast.error(result.error.message || "Failed to update status");
        return;
      }

      const successMessage =
        result.response?.message ||
        result.response?.success ||
        "Status updated successfully";
      toast.success(successMessage);

      // Close dropdown
      setOpenStatusDropdown(null);

      // Refresh the returns list
      fetchReturns();
      // Refresh stats
      fetchReturnStats();
    } catch (error) {
      console.error("Error updating status:", error);
      // Error already shown in toast above
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDeleteClick = (returnItem) => {
    setReturnToDelete(returnItem);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async (returnItem) => {
    try {
      // Use the numeric ID from API response (not return_id which is like "RTN-001")
      const returnId = returnItem.id;
      if (!returnId) {
        toast.error("Invalid return ID");
        return;
      }

      const result = await returnRegisterAPI.deleteReturn(returnId);
      if (result.error) {
        // Show backend error message
        toast.error(result.error.message || "Failed to delete return");
        return;
      }

      // Show backend success message if available, otherwise default
      const successMessage =
        result.response?.message ||
        result.response?.success ||
        "Return deleted successfully";
      toast.success(successMessage);
      // Refresh the returns list
      fetchReturns();
    } catch (error) {
      console.error("Error deleting return:", error);
      // Error already shown in toast above
    } finally {
      setShowDeleteModal(false);
      setReturnToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setReturnToDelete(null);
  };

  return (
    <>
      {/* Content Area */}
      <div className="card rounded-3 p-4 border-0 mb-1 returns-container">
        <div className="returns-layout">
          {/* Header */}
          <div className="returns-top">
            <div className="returns-header">
              <div>
                <h2 className="returns-title">Register Returns</h2>
                <p className="returns-subtitle">
                  Register and track customers’ returned devices
                </p>
              </div>

              <div className="d-flex gap-2 align-items-center">
                {/* Scan Button */}
                <button
                  className={`${
                    isPartsURL ? "scan-returns-btn-parts" : "scan-returns-btn"
                  }`}
                  onClick={() => setShowForm(true)}
                >
                  <ScanIcon fill={isPartsURL ? "var(--primary-color-parts)" : "var(--primary-color-mobile)"} /> Scan
                </button>

                {/* Add Return Button */}
                <button
                  className={`${
                    isPartsURL ? "add-returns-btn-parts" : "add-returns-btn"
                  }`}
                  onClick={() => setShowAddManualModal(true)}
                >
                  <AddIcon /> Add Manual Returns
                </button>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="row mb-2">
              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Active Registration
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          formatNumber(activeRegistrationCount)
                        )}
                      </h4>
                    </div>

                    <div className="d-flex align-items-center justify-content-center">
                      <img
                        src={ActiveRegistrationIcon}
                        alt="Active Registration"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Completed
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          formatNumber(completedCount)
                        )}
                      </h4>
                    </div>

                    <div className="d-flex align-items-center justify-content-center">
                      <img src={CompletedIcon} alt="Completed" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Under Repair
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          formatNumber(underRepairCount)
                        )}
                      </h4>
                    </div>
                    <div className="d-flex align-items-center justify-content-center">
                      <img src={UnderRepairIcon} alt="Under Repair" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Not repairable
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          formatNumber(notRepairableCount)
                        )}
                      </h4>
                    </div>

                    <div className="d-flex align-items-center justify-content-center">
                      <img src={NotRepairableIcon} alt="Not Repairable" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Search + Date Filter + Status Filter */}
            <div className="search-filter-section mb-4">
              {/* Search Bar */}
              <div className="returns-search-container">
                <input
                  type="text"
                  className={`${
                    isPartsURL
                      ? "returns-search-input-parts"
                      : "returns-search-input"
                  }`}
                  placeholder="Search by Return ID, IMEI, customer name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <img
                  src={SearchIcon}
                  alt="Search"
                  className="returns-search-icon"
                />
              </div>

              {/* Date Filter */}
              <div
                className="returns-filter-dropdown"
                ref={dateFilterDropdownRef}
              >
                <button
                  className={`${
                    isPartsURL
                      ? "returns-date-filter-btn-parts"
                      : "returns-date-filter-btn"
                  }`}
                  onClick={() =>
                    setShowDateFilterDropdown(!showDateFilterDropdown)
                  }
                >
                  <img src={CalendarIcon} alt="Calendar" />
                  {getDateFilterLabel()}
                  {showDateFilterDropdown ? (
                    <ChevronUp
                      size={16}
                      className="returns-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  ) : (
                    <ChevronDown
                      size={16}
                      className="returns-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  )}
                </button>

                <div
                  className={`returns-filter-dropdown-content ${
                    showDateFilterDropdown ? "show" : ""
                  }`}
                >
                  {dateFilterOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`returns-filter-dropdown-item ${
                        dateFilter === option.value ? "selected" : ""
                      }`}
                      onClick={() => handleDateFilterSelect(option.value)}
                    >
                      <Check
                        size={16}
                        className="returns-filter-dropdown-check"
                      />
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Statuses Filter */}
              <div className="returns-filter-dropdown" ref={filterDropdownRef}>
                <button
                  className={`${
                    isPartsURL
                      ? "returns-statuses-filter-btn-parts"
                      : "returns-statuses-filter-btn"
                  }`}
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                >
                  <FilterIcon size={16} />
                  {getStatusFilterLabel()}
                  {showFilterDropdown ? (
                    <ChevronUp
                      size={16}
                      className="returns-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  ) : (
                    <ChevronDown
                      size={16}
                      className="returns-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  )}
                </button>

                <div
                  className={`returns-filter-dropdown-content ${
                    showFilterDropdown ? "show" : ""
                  }`}
                >
                  {statusFilterOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`returns-filter-dropdown-item ${
                        statusFilter === option.value ? "selected" : ""
                      }`}
                      onClick={() => handleStatusFilterSelect(option.value)}
                    >
                      <Check
                        size={16}
                        className="returns-filter-dropdown-check"
                      />
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Returns Table */}
          <div className="returns-table-wrapper">
            <div className="returns-table-responsive">
              <table className="returns-table">
                <thead>
                  <tr>
                    <th>RETURN ID</th>
                    <th>MODEL</th>
                    <th>CUSTOMER</th>
                    <th>DEFECT NOTE</th>
                    <th>STATUS</th>
                    <th>DATE</th>
                    <th style={{ textAlign: "center" }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="no-products-message">
                        <div
                          className=""
                          style={{
                            marginBottom: "4rem",
                            marginTop: "4rem",
                          }}
                        >
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <h3 style={{ marginTop: "2rem" }}>
                            Loading returns...
                          </h3>
                        </div>
                      </td>
                    </tr>
                  ) : currentReturns.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="no-products-message">
                        <div
                          className=""
                          style={{
                            marginBottom: "4rem",
                            marginTop: "4rem",
                          }}
                        >
                          <NoDataIcon />
                          <h3 style={{ marginTop: "2rem" }}>
                            No returns found
                          </h3>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentReturns.map((returnItem) => {
                      // Map API response fields
                      const model =
                        returnItem.product_name || returnItem.model || "-";
                      const customer =
                        returnItem.customer_name || returnItem.customer || "-";
                      const defectNote =
                        returnItem.note ||
                        returnItem.defect_note ||
                        returnItem.defect ||
                        returnItem.notes ||
                        "-";
                      const returnId =
                        returnItem.return_id || returnItem.id || "-";

                      const modelTruncated = truncateText(model, 20);
                      const customerTruncated = truncateText(customer, 10);
                      const defectNoteTruncated = truncateText(defectNote, 10);

                      const modelString = String(model || "");
                      const customerString = String(customer || "");
                      const defectNoteString = String(defectNote || "");

                      const isModelTruncated =
                        model !== "-" && modelString.length > 20;
                      const showCustomerTooltip =
                        customer !== "-" && customerString.length > 10;
                      const showDefectNoteTooltip =
                        defectNote !== "-" && defectNoteString.length > 10;

                      // Get product details
                      const product = returnItem.product || {};
                      const productDetails = [];
                      if (product.ram) productDetails.push(product.ram);
                      if (product.storage) productDetails.push(product.storage);
                      if (product.variants && product.variants.length > 0) {
                        productDetails.push(
                          product.variants
                            .map((variant) => Object.values(variant).join(", "))
                            .join(", ")
                        );
                      }
                      if (product.suffix) productDetails.push(product.suffix);
                      if (
                        product.used_mobile !== undefined &&
                        product.used_mobile !== null
                      ) {
                        productDetails.push(
                          product.used_mobile === false ? "New" : "Used"
                        );
                      }
                      const productDetailsText = productDetails.join(" | ");

                      // Create tooltip content with product name and details
                      const modelTooltipContent = (
                        <div>
                          <div>{model}</div>
                          {productDetailsText && (
                            <div
                              style={{
                                fontSize: "0.8em",
                                marginTop: "4px",
                              }}
                            >
                              {productDetailsText}
                            </div>
                          )}
                        </div>
                      );

                      return (
                        <tr key={returnItem.id || returnItem.return_id}>
                          <td>{returnId}</td>
                          <td>
                            {/* Model tooltip */}
                            {isModelTruncated ? (
                              // Show tooltip if model is truncated
                              <Tooltip
                                content={modelTooltipContent}
                                show={isModelTruncated}
                              >
                                <span className="returns-table-cell-truncate">
                                  {modelTruncated}
                                </span>
                              </Tooltip>
                            ) : productDetailsText ? (
                              // Show details directly in cell if model is not truncated
                              <div>
                                <div>{model}</div>
                                <div
                                  style={{
                                    fontSize: "0.8em",
                                    marginTop: "4px",
                                  }}
                                >
                                  {productDetailsText}
                                </div>
                              </div>
                            ) : (
                              // Just show model name if no details
                              <span className="returns-table-cell-truncate">
                                {modelTruncated}
                              </span>
                            )}
                          </td>
                          <td>
                            <Tooltip
                              content={customer}
                              show={showCustomerTooltip}
                            >
                              <span className="returns-table-cell-truncate">
                                {customerTruncated}
                              </span>
                            </Tooltip>
                          </td>
                          <td>
                            <Tooltip
                              content={defectNote}
                              show={showDefectNoteTooltip}
                            >
                              <span className="returns-table-cell-truncate">
                                {defectNoteTruncated}
                              </span>
                            </Tooltip>
                          </td>

                          {/* Status Dropdown */}
                          <td>
                            {(() => {
                              const status = (
                                returnItem.status || ""
                              ).toLowerCase();
                              const returnId = returnItem.id;
                              const isDropdownOpen =
                                openStatusDropdown === returnId;
                              const isUpdating = updatingStatus === returnId;
                              // Hide dropdown for "Successfully Returned" and "Refund" statuses
                              const normalizedStatus = status
                                .trim()
                                .replace(/\s+/g, "_");
                              const showDropdown =
                                normalizedStatus !== "refund" &&
                                normalizedStatus !== "successfully_returned";

                              // Get status badge config
                              let badgeConfig = null;
                              if (
                                status === "under_repair" ||
                                status === "under repair"
                              ) {
                                badgeConfig = {
                                  className: "under-repair-status-badge",
                                  color: "#8A38F5",
                                  label: "Under Repair",
                                };
                              } else if (
                                status === "not_repairable" ||
                                status === "not repairable"
                              ) {
                                badgeConfig = {
                                  className: "not-repairable-status-badge",
                                  color: "#FF4564",
                                  label: "Not Repairable",
                                };
                              } else if (
                                status === "in_process" ||
                                status === "in process"
                              ) {
                                badgeConfig = {
                                  className: "in-process-status-badge",
                                  color: "#57606A",
                                  label: "In Process",
                                };
                              } else if (status === "completed") {
                                badgeConfig = {
                                  className: "completed-status-badge",
                                  color: "#2F80ED",
                                  label: "Completed",
                                };
                              } else if (
                                status === "successfully_returned" ||
                                status === "successfully returned"
                              ) {
                                return (
                                  <div className="d-flex align-items-center gap-2 successfully-returned-status-badge">
                                    <div
                                      style={{
                                        width: "8px",
                                        height: "8px",
                                        backgroundColor: "#0A8900",
                                        borderRadius: "100%",
                                      }}
                                    />
                                    <span>Successfully Returned</span>
                                  </div>
                                );
                              } else if (
                                status === "refund to customer" ||
                                status === "refund"
                              ) {
                                return (
                                  <div className="d-flex align-items-center gap-2 refund-status-badge">
                                    <div
                                      style={{
                                        width: "8px",
                                        height: "8px",
                                        backgroundColor: "#F49B35",
                                        borderRadius: "100%",
                                      }}
                                    />
                                    <span>Refund to Customer</span>
                                  </div>
                                );
                              } else {
                                return "--";
                              }

                              if (!badgeConfig) return null;

                              return (
                                <div style={{ position: "relative" }}>
                                  <div
                                    ref={(el) => {
                                      if (el) {
                                        statusTriggerRefs.current[returnId] =
                                          el;
                                      }
                                    }}
                                    className={`d-flex align-items-center gap-2 ${badgeConfig.className}`}
                                    style={{
                                      cursor: showDropdown
                                        ? "pointer"
                                        : "default",
                                      opacity: isUpdating ? 0.6 : 1,
                                    }}
                                    onClick={(e) => {
                                      if (showDropdown && !isUpdating) {
                                        e.stopPropagation();
                                        if (isDropdownOpen) {
                                          setOpenStatusDropdown(null);
                                        } else {
                                          // Calculate position for fixed dropdown
                                          const triggerEl =
                                            statusTriggerRefs.current[returnId];
                                          if (triggerEl) {
                                            const rect =
                                              triggerEl.getBoundingClientRect();
                                            setDropdownPosition({
                                              top: rect.bottom + 4,
                                              left: rect.left,
                                            });
                                          }
                                          setOpenStatusDropdown(returnId);
                                        }
                                      }
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: "8px",
                                        height: "8px",
                                        backgroundColor: badgeConfig.color,
                                        borderRadius: "100%",
                                      }}
                                    />
                                    <span>{badgeConfig.label}</span>
                                    {showDropdown && (
                                      <ChevronDown
                                        size={16}
                                        className=""
                                        style={{
                                          width: "16px",
                                          height: "16px",
                                          color: badgeConfig.color,
                                          transform: isDropdownOpen
                                            ? "rotate(180deg)"
                                            : "rotate(0deg)",
                                          transition: "transform 0.2s",
                                        }}
                                      />
                                    )}
                                  </div>

                                  {/* Status Dropdown */}
                                  {showDropdown && isDropdownOpen && (
                                    <div
                                      className="returns-status-dropdown-content"
                                      ref={(el) => {
                                        if (el) {
                                          statusDropdownRefs.current[returnId] =
                                            el;
                                        }
                                      }}
                                      style={{
                                        position: "fixed",
                                        top: `${dropdownPosition.top}px`,
                                        left: `${dropdownPosition.left}px`,
                                        zIndex: 9999,
                                        backgroundColor: "#fff",
                                        border: "1px solid #DCE3EB",
                                        borderRadius: "8px",
                                        boxShadow:
                                          "0 4px 6px rgba(0, 0, 0, 0.1)",
                                        minWidth: "180px",
                                      }}
                                    >
                                      {statusDropdownOptions.map((option) => (
                                        <div
                                          key={option.value}
                                          className={`returns-status-dropdown-item ${
                                            badgeConfig.label === option.label
                                              ? "selected"
                                              : ""
                                          }`}
                                          style={{
                                            padding: "10px 16px",
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            backgroundColor:
                                              badgeConfig.label === option.label
                                                ? "#F5F7FA"
                                                : "transparent",
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (
                                              badgeConfig.label !== option.label
                                            ) {
                                              handleStatusUpdate(
                                                returnId,
                                                option.value
                                              );
                                            } else {
                                              setOpenStatusDropdown(null);
                                            }
                                          }}
                                        >
                                          <span
                                            style={{
                                              color:
                                                badgeConfig.label ===
                                                option.label
                                                  ? "#8C959F"
                                                  : "inherit",
                                            }}
                                          >
                                            {option.label}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </td>

                          {/* Register Date */}
                          <td>{formatReturnDate(returnItem.updated_at)}</td>

                          <td>
                            <div className="action-buttons">
                              <button
                                className={`${
                                  isPartsURL
                                    ? "action-btn-parts view"
                                    : "action-btn view"
                                }`}
                                onClick={() => {
                                  setEditingReturn(returnItem);
                                  setShowAddManualModal(true);
                                }}
                                title="Edit return"
                              >
                                <EditIcon
                                  fill={isPartsURL ? "var(--primary-color-parts)" : "var(--primary-color-mobile)"}
                                />
                              </button>
                              <button
                                className={`${
                                  isPartsURL
                                    ? "action-btn-parts view"
                                    : "action-btn view"
                                }`}
                                onClick={() => {
                                  setSelectedReturn(returnItem);
                                  setShowPrintModal(true);
                                }}
                                title="Print return"
                              >
                                <PrintIcon
                                  fill={isPartsURL ? "var(--primary-color-parts)" : "var(--primary-color-mobile)"}
                                />
                              </button>
                              <button
                                className="action-btn delete"
                                onClick={() => handleDeleteClick(returnItem)}
                                title="Delete return"
                              >
                                <RedDeleteIcon className="action-icon delete" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {shouldShowPagination && (
            <div className="returns-pagination-sticky">
              <div className="returns-pagination-container">
                <div className="returns-pagination-info">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="returns-pagination-buttons">
                  <button
                    className="previous"
                    onClick={() => {
                      const newPage = currentPage - 1;
                      setCurrentPage(newPage);
                    }}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <button
                    className={`${
                      isPartsURL
                        ? "next-returns-pagination-btn-parts"
                        : "next-returns-pagination-btn"
                    }`}
                    onClick={() => {
                      const newPage = currentPage + 1;
                      setCurrentPage(newPage);
                    }}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Print Return Register Modal */}
      <PrintReturnRegister
        isOpen={showPrintModal}
        onClose={() => {
          setShowPrintModal(false);
          setSelectedReturn(null);
        }}
        returnData={selectedReturn}
      />

      {/* Delete Modal */}
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        returnItem={returnToDelete}
      />

      {/* Add Manual Returns Modal */}
      <AddManualReturnsModal
        isOpen={showAddManualModal}
        onClose={() => {
          setShowAddManualModal(false);
          setEditingReturn(null);
        }}
        editReturn={editingReturn}
        onSubmit={async (formData, returnId) => {
          try {
            let result;
            if (returnId) {
              // Update existing return
              result = await returnRegisterAPI.updateReturn(returnId, formData);
              if (result.error) {
                toast.error(result.error.message || "Failed to update return");
                throw new Error(result.error.message);
              }

              const successMessage =
                result.response?.message ||
                result.response?.success ||
                "Return updated successfully";
              toast.success(successMessage);
            } else {
              // Create new return
              result = await returnRegisterAPI.createReturn(formData);
              if (result.error) {
                toast.error(
                  result.error.message || "Failed to register return"
                );
                throw new Error(result.error.message);
              }

              const successMessage =
                result.response?.message ||
                result.response?.success ||
                "Return registered successfully";
              toast.success(successMessage);
            }

            setShowAddManualModal(false);
            setEditingReturn(null);
            // Refresh the returns list
            fetchReturns();
          } catch (error) {
            console.error("Error saving return:", error);
            // Error already shown in toast above
            throw error;
          }
        }}
      />
    </>
  );
}

export default RegisterReturns;
